import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './crm-ui.scss';

const commands = [
  ['bold', 'B'],
  ['italic', 'I'],
  ['underline', 'U'],
];

function WgiymEditor({ value = '', onChange, placeholder = 'Write content…' }) {
  const editorRef = useRef(null);
  const focusedRef = useRef(false);
  const savedRangeRef = useRef(null);
  const [active, setActive] = useState({});

  useLayoutEffect(() => {
    const editor = editorRef.current;
    if (!editor || focusedRef.current) return;
    const next = value || '';
    if (editor.innerHTML !== next) editor.innerHTML = next;
  }, [value]);

  const emit = useCallback(() => {
    const html = editorRef.current?.innerHTML || '';
    onChange?.(html);
  }, [onChange]);

  const selectionBelongsToEditor = useCallback((selection) => {
    const editor = editorRef.current;
    if (!editor || !selection?.rangeCount) return false;
    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer.nodeType === Node.TEXT_NODE
      ? range.commonAncestorContainer.parentNode
      : range.commonAncestorContainer;
    return container === editor || editor.contains(container);
  }, []);

  const refreshActiveState = useCallback(() => {
    const selection = window.getSelection();
    if (!selectionBelongsToEditor(selection)) return;
    try {
      setActive({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        insertUnorderedList: document.queryCommandState('insertUnorderedList'),
        insertOrderedList: document.queryCommandState('insertOrderedList'),
        h2: String(document.queryCommandValue('formatBlock') || '').toLowerCase().replace(/[<>]/g, '') === 'h2',
      });
    } catch {
      // Older browsers can throw for queryCommandState when there is no editable selection.
    }
  }, [selectionBelongsToEditor]);

  const rememberSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selectionBelongsToEditor(selection)) return;
    savedRangeRef.current = selection.getRangeAt(0).cloneRange();
    refreshActiveState();
  }, [refreshActiveState, selectionBelongsToEditor]);

  useEffect(() => {
    document.addEventListener('selectionchange', rememberSelection);
    return () => document.removeEventListener('selectionchange', rememberSelection);
  }, [rememberSelection]);

  const restoreSelection = () => {
    const range = savedRangeRef.current;
    if (!range) return;
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const run = (command, argument = null) => {
    editorRef.current?.focus();
    restoreSelection();
    document.execCommand(command, false, argument);
    rememberSelection();
    emit();
  };

  const toolbarButtonProps = (command, extraActive = false) => ({
    type: 'button',
    onMouseDown: (event) => event.preventDefault(),
    className: active[command] || extraActive ? 'is-active' : '',
    'aria-pressed': Boolean(active[command] || extraActive),
  });

  const handleBlur = () => {
    focusedRef.current = false;
    emit();
  };

  return (
    <div className="wgiym-editor">
      <div className="wgiym-editor__toolbar" role="toolbar" aria-label="Text formatting">
        {commands.map(([command, label]) => (
          <button key={command} {...toolbarButtonProps(command)} onClick={() => run(command)} aria-label={command}>
            {label}
          </button>
        ))}
        <button {...toolbarButtonProps('h2')} onClick={() => run('formatBlock', active.h2 ? 'p' : 'h2')}>H2</button>
        <button {...toolbarButtonProps('insertUnorderedList')} onClick={() => run('insertUnorderedList')}>• List</button>
        <button {...toolbarButtonProps('insertOrderedList')} onClick={() => run('insertOrderedList')}>1. List</button>
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            const url = window.prompt('Link URL');
            if (url) run('createLink', url);
          }}
        >
          Link
        </button>
        <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => run('removeFormat')}>Clear</button>
      </div>
      <div
        ref={editorRef}
        className="wgiym-editor__surface"
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onFocus={() => { focusedRef.current = true; rememberSelection(); }}
        onInput={() => { rememberSelection(); emit(); }}
        onKeyUp={rememberSelection}
        onMouseUp={rememberSelection}
        onBlur={handleBlur}
      />
    </div>
  );
}

export default WgiymEditor;
